"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const EXPORT_BY_REPORT = [
  {
    report: "General Ledger",
    formats: "PDF and XLSX",
  },
  {
    report: "Item Ledger",
    formats: "PDF and XLSX",
  },
  {
    report: "Bank Ledger",
    formats: "PDF and XLSX",
  },
  {
    report: "Sales Ledger (monthly summary)",
    formats: "PDF only",
  },
  {
    report: "Sales Ledger (month detail)",
    formats: "PDF and XLSX",
  },
  {
    report: "Purchase Ledger (monthly summary)",
    formats: "PDF only",
  },
  {
    report: "Purchase Ledger (month detail)",
    formats: "PDF and XLSX",
  },
  {
    report: "Creditors Ledger (list and party detail)",
    formats: "PDF and XLSX",
  },
  {
    report: "Debtors Ledger (list and party detail)",
    formats: "PDF and XLSX",
  },
  {
    report: "Gross Profit",
    formats: "No export on this screen",
  },
  {
    report: "Customer Aging / Vendor Aging",
    formats: "No export on this screen",
  },
  {
    report: "Receivables / Payables",
    formats: "No export on this screen",
  },
];

const RULES = [
  {
    topic: "Catalog is the source of truth",
    rule: "Reports → Reports lists only the rows below. Profit & Loss sheet, Balance Sheet, and GSTR-1 are not on this catalog — do not look for them here",
  },
  {
    topic: "General Ledger is a party statement",
    rule: "Reports → General Ledger needs a Customer (picker shows Role). It is not a Chart of Accounts all-accounts dump. CoA row click is the account statement. Accountant → Account Ledger is a named master (ACCPA), not this report",
  },
  {
    topic: "Apply Filters loads the data",
    rule: "Changing a dropdown does not refresh the table. Click Apply Filters. Reset All clears the current report's filters. View Filters / Hide Filters toggles the filter bar on most reports",
  },
  {
    topic: "What is in a ledger",
    rule: "Reports read posted books data when you run them. A Draft invoice, Pending transfer, or Draft adjustment does not appear until that document posts. There is no overnight batch",
  },
  {
    topic: "Export is per report",
    rule: "Not every report has PDF and Excel. Gross Profit, Aging, and Receivables / Payables have no Export Report. Sales and Purchase monthly summaries are PDF only; their month detail can also export XLSX",
  },
  {
    topic: "Permissions",
    rule: "Each report has its own view permission. A group with no allowed reports is hidden from the catalog. Created By is always System Generated",
  },
  {
    topic: "Branch follows Division",
    rule: "Where both exist, pick Division first. Branch is disabled until Division is set, and the list is branches of those divisions",
  },
];

const sections = [
  {
    heading: "What Reports is",
    subsections: [
      { heading: "1. Open the catalog" },
      { heading: "2. Filters, Apply, and date presets" },
      { heading: "3. Export Report" },
    ],
  },
  {
    heading: "The reports",
    subsections: [
      { heading: "1. Accountant — General Ledger" },
      { heading: "2. Item — Item Ledger" },
      { heading: "3. Bank — Bank Ledger" },
      { heading: "4. Sales — Sales Ledger" },
      { heading: "5. Purchase Ledger" },
      { heading: "6. Creditors Ledger" },
      { heading: "7. Debtors Ledger" },
      { heading: "8. Gross Profit" },
      { heading: "9. Aging Reports" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

const Steps = ({ items }) => (
  <ol className="space-y-3 mb-4">
    {items.map((text, i) => (
      <li key={i} className="flex items-start gap-3 list-none">
        <span className="w-6 h-6 rounded-full bg-[#111827] text-white text-[12px] geist-medium flex items-center justify-center flex-shrink-0 mt-0.5">
          {i + 1}
        </span>
        <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
          {text}
        </p>
      </li>
    ))}
  </ol>
);

const Flow = ({ steps }) => (
  <p className="text-[15px] geist-regular text-[#374151] leading-relaxed mb-4 px-4 py-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-[10px]">
    {steps.map((step, i) => (
      <span key={`${step}-${i}`}>
        {i > 0 && (
          <span className="text-[#9CA3AF] geist-medium"> {"→"} </span>
        )}
        <span className="geist-medium text-[#111827]">{step}</span>
      </span>
    ))}
  </p>
);

export default function ReportsOverviewPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Reports
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone who needs a ledger, aging buckets, or gross profit from
            posted books — not charts
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            This module is a catalog of ledgers, aging, and gross profit. You
            pick a report, set filters, then run it. The table is posted books
            data at that moment — not Analytics charts, and not an overnight
            batch.
          </p>
          <Flow
            steps={[
              "Sidebar",
              "Reports",
              "click a report",
              "set filters",
              "Apply Filters",
              "read or Export Report",
            ]}
          />
          <Callout type="note">
            Listed here: General Ledger, Item Ledger, Bank Ledger, Sales
            Ledger, Purchase Ledger, Creditors Ledger, Debtors Ledger, Gross
            Profit, Customer Aging, Vendor Aging, Receivables / Payables.
            Profit & Loss sheet, Balance Sheet, and GSTR-1 are not on this
            list.
          </Callout>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What Reports is
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Open the catalog
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Title is <B>Reports</B>. Columns are <B>Report Name</B>,{" "}
            <B>Type</B>, <B>Created By</B> (always System Generated). Grey
            header rows are groups with a count.
          </p>
          <Steps
            items={[
              <>
                Open <B>Reports</B> in the left sidebar.
              </>,
              <>
                Find the group, then click the <B>Report Name</B> to open that
                report.
              </>,
              <>
                Use the back arrow on any report to return to this catalog.
              </>,
            ]}
          />
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Path into each group:
          </p>
          <Definitions
            items={[
              { term: "Accountant", desc: "Accountant → General Ledger" },
              { term: "Item", desc: "Item → Item Ledger" },
              { term: "Bank", desc: "Bank → Bank Ledger" },
              { term: "Sales", desc: "Sales → Sales Ledger" },
              {
                term: "Purchase Ledger",
                desc: "Purchase Ledger → Purchase Ledger",
              },
              {
                term: "Creditors Ledger",
                desc: "Creditors Ledger → Creditors Ledger",
              },
              {
                term: "Debtors Ledger",
                desc: "Debtors Ledger → Debtors Ledger",
              },
              { term: "Gross Profit", desc: "Gross Profit → Gross Profit" },
              {
                term: "Aging Reports",
                desc: "Aging Reports → Customer Aging Report, Vendor Aging Report, or Receivables / Payables",
              },
            ]}
          />
          <Callout type="tip">
            A missing report means your role does not have view on it. If no
            child in a group is allowed, the whole group is hidden.
          </Callout>
          <Screenshot caption="Reports catalog — grouped table of Report Name, Type, Created By" />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Filters, Apply, and date presets
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Changing a dropdown does not load the table. Set the filters, then
            run the report.
          </p>
          <Steps
            items={[
              <>
                If the bar is hidden, click <B>View Filters</B> (toggles to{" "}
                <B>Hide Filters</B>).
              </>,
              <>
                Fill the required picker first where the report has one —
                Customer, Item, or Bank. Apply does not skip a required field.
              </>,
              <>
                Where both exist: <B>Division</B> → <B>Branch</B>. Branch stays
                disabled until Division is set, and only shows branches of
                those divisions.
              </>,
              <>
                Set the date range or <B>Financial Year</B>, then click{" "}
                <B>Apply Filters</B>.
              </>,
              <>
                Active filters show as tags under the bar. Clear one tag, or
                click <B>Reset All</B> to clear the report.
              </>,
            ]}
          />
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-2">
            Date presets on a range picker:
          </p>
          <Flow
            steps={[
              "Today",
              "Last 7 Days",
              "Last 15 Days",
              "Last Month",
              "Last 3 Months",
              "Custom Range",
              "Specific Date",
            ]}
          />
          <Definitions
            items={[
              {
                term: "Gross Profit → Customer's Month Wise",
                desc: "Month presets instead: Last 3 Months → Last 6 Months → Last 9 Months → Last 12 Months → Custom Range",
              },
              {
                term: "Sales Ledger and Purchase Ledger summaries",
                desc: "Financial Year (April–March, current FY by default) — not the date-range picker. Month detail uses a date range for that month",
              },
            ]}
          />
          <Callout type="warning">
            General Ledger → Customer is required. Item Ledger → Item is
            required. Bank Ledger → Bank is required. The screen shows the
            error; Apply Filters will not run without it.
          </Callout>

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Export Report
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Export is not on every report. Where it exists, use this path:
          </p>
          <Flow
            steps={[
              "Export Report",
              "PDF or XLSX",
              "filename in the modal",
              "export",
            ]}
          />
          <Steps
            items={[
              <>
                Open <B>Export Report</B> in the header. Only the formats that
                report supports appear (table below).
              </>,
              <>
                The modal asks for a filename. Default is the report name plus
                today&apos;s date. PDF uses the organisation profile.
              </>,
              <>
                Sales Ledger and Purchase Ledger month detail also offer XLSX
                through an export-data modal. Their monthly summary is PDF
                only.
              </>,
            ]}
          />
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    Report
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    Export
                  </th>
                </tr>
              </thead>
              <tbody>
                {EXPORT_BY_REPORT.map((row) => (
                  <tr
                    key={row.report}
                    className="border-b border-[#E5E5E5] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 text-[14px] geist-medium text-[#111827] align-top">
                      {row.report}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151]">
                      {row.formats}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            The reports
          </h2>

          <h3
            id="section-1-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Accountant — General Ledger
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Party-wise debit / credit statement for one contact. Opening
            balance, period lines, closing balance. Extra pages show Brought
            Forward / Carried Over. You can also land here from a customer with{" "}
            <B>customerId</B> already set.
          </p>
          <Flow
            steps={[
              "Reports",
              "General Ledger",
              "Customer",
              "date / Division / Branch",
              "Apply Filters",
            ]}
          />
          <Steps
            items={[
              <>
                Pick <B>Customer</B> (required). The picker shows Customer ID,
                Customer Name, and Role.
              </>,
              <>
                Set date range, then <B>Division</B> → <B>Branch</B>. If the
                party is linked as vendor and/or customer, a switch includes
                the linked data.
              </>,
              <>
                Click <B>Apply Filters</B>. Click a voucher row to open the
                source document. <B>Export Report</B> → PDF or XLSX.
              </>,
            ]}
          />
          <Definitions
            items={[
              {
                term: "Columns",
                desc: "Date, Particulars, Vch Type, Vch No, Debit, Credit, Balance",
              },
              { term: "Export", desc: "PDF and XLSX" },
            ]}
          />
          <Screenshot caption="General Ledger — Customer required, date range, Division, Branch, Apply Filters, Export Report" />

          <h3
            id="section-1-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Item — Item Ledger
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Quantity in and out for one item over a date range, with opening
            and closing qty rows. This is the period movement report. View
            Inventory is on-hand qty and value with no date range.
          </p>
          <Flow
            steps={[
              "Reports",
              "Item Ledger",
              "Item",
              "date / Division / Branch",
              "Apply Filters",
            ]}
          />
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-2">
            Other way in:
          </p>
          <Flow steps={["View Inventory", "View Ledger", "this report for that item"]} />
          <Steps
            items={[
              <>
                Pick <B>Item</B> (required), then date range, Division, Branch.
                Click <B>Apply Filters</B>.
              </>,
              <>
                Click <B>Doc No</B> to open the posting document (Quote, Sales
                Order, Delivery Challan, Invoice, Purchase Order, GRPO, Bill,
                Production, Credit Note, Debit Note, Transfer, and the others
                that actually posted that line).
              </>,
              <>
                <B>Export Report</B> → PDF or XLSX.
              </>,
            ]}
          />
          <Definitions
            items={[
              {
                term: "Columns",
                desc: "Date, Effect (IN / OUT), Posting Document, Doc No, Vendor/Customer Name, QTY, QTY Balance, Batch Item Price, Amount, Balance, Warehouse",
              },
              { term: "Export", desc: "PDF and XLSX" },
            ]}
          />
          <Screenshot caption="Item Ledger — Item required, Effect IN/OUT, qty and running balance" />

          <h3
            id="section-1-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Bank — Bank Ledger
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Statement for one bank account: opening balance, movements,
            available balance.
          </p>
          <Flow
            steps={[
              "Reports",
              "Bank Ledger",
              "Bank",
              "date / Division / Branch",
              "Apply Filters",
            ]}
          />
          <Steps
            items={[
              <>
                Pick <B>Bank</B> (required), then date range, Division, Branch.
                Click <B>Apply Filters</B>.
              </>,
              <>
                <B>Export Report</B> → PDF or XLSX.
              </>,
            ]}
          />
          <Definitions
            items={[
              {
                term: "Columns",
                desc: "Bank Date, Transaction Details, Effect, Document, Doc no., Branch, Division, Amount, Available Balance",
              },
              { term: "Export", desc: "PDF and XLSX" },
            ]}
          />
          <Screenshot caption="Bank Ledger — Bank required, date range, Division, Branch" />

          <h3
            id="section-1-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Sales — Sales Ledger
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Two screens. First is a financial-year monthly summary with a
            column chart. Click a month for that month&apos;s invoices. There
            is no GST column set and no invoice-status filter.
          </p>
          <Flow
            steps={[
              "Reports",
              "Sales Ledger",
              "Financial Year / Division / Branch",
              "Apply Filters",
              "click a month",
              "invoice lines",
            ]}
          />
          <Steps
            items={[
              <>
                Set <B>Financial Year</B> (April–March, current FY by default),
                then Division → Branch. Click <B>Apply Filters</B>.
              </>,
              <>
                Read the monthly table and chart. Export this summary as PDF
                only.
              </>,
              <>
                Click a month. Detail uses that month&apos;s date range plus
                Division / Branch. Export detail as PDF or XLSX.
              </>,
            ]}
          />
          <Definitions
            items={[
              {
                term: "Summary columns",
                desc: "Month, Total Orders, Basic Amount, Total Amount (with Tax), Closing Balance",
              },
              {
                term: "Month detail columns",
                desc: "Date, Particulars, Vch Type (Tax Invoice), Vch No., Debit, Credit",
              },
            ]}
          />
          <Screenshot caption="Sales Ledger — Financial Year, monthly totals and chart; click a month for invoice lines" />

          <h3
            id="section-1-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Purchase Ledger
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Same shape as Sales Ledger, for bills: FY monthly summary, then
            bill lines for the month you click.
          </p>
          <Flow
            steps={[
              "Reports",
              "Purchase Ledger",
              "Financial Year / Division / Branch",
              "Apply Filters",
              "click a month",
              "bill lines",
            ]}
          />
          <Steps
            items={[
              <>
                Set <B>Financial Year</B>, Division → Branch. Click{" "}
                <B>Apply Filters</B>.
              </>,
              <>Export the monthly summary as PDF only.</>,
              <>
                Click a month for bill lines. Export that detail as PDF or
                XLSX.
              </>,
            ]}
          />
          <Definitions
            items={[
              {
                term: "Summary columns",
                desc: "Month, Total Orders, Sub Total, Total Amount, Closing Balance",
              },
              {
                term: "Month detail columns",
                desc: "Date, Particulars, Vch Type (Bill), Vch No., Debit, Credit",
              },
            ]}
          />
          <Screenshot caption="Purchase Ledger — Financial Year monthly summary, then bill detail for the month" />

          <h3
            id="section-1-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Creditors Ledger
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Vendor-wise balances. Total / Brought Forward / Carried Over stay
            on the list. Click a vendor for that party&apos;s statement (same
            debit/credit layout as General Ledger).
          </p>
          <Flow
            steps={[
              "Reports",
              "Creditors Ledger",
              "date / Type / Division / Branch",
              "Apply Filters",
              "click a vendor",
              "party statement",
            ]}
          />
          <Steps
            items={[
              <>
                Set date range, <B>Type</B> (CREDIT or DEBIT),{" "}
                <B>Record With Zero Balance</B> if you need zero rows, then
                Division → Branch. Click <B>Apply Filters</B>.
              </>,
              <>
                Click a vendor row for the statement.{" "}
                <B>Export Report</B> → PDF or XLSX on both the list and the
                party detail.
              </>,
            ]}
          />
          <Definitions
            items={[
              {
                term: "List columns",
                desc: "Particulars, Debit, Credit, Due Amount",
              },
            ]}
          />
          <Screenshot caption="Creditors Ledger — Type CREDIT/DEBIT, zero-balance switch, click a vendor for the statement" />

          <h3
            id="section-1-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. Debtors Ledger
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Customer-wise balances. Same filter bar and click-through as
            Creditors, for customers.
          </p>
          <Flow
            steps={[
              "Reports",
              "Debtors Ledger",
              "date / Type / Division / Branch",
              "Apply Filters",
              "click a customer",
              "party statement",
            ]}
          />
          <Steps
            items={[
              <>
                Set date range, <B>Type</B> (CREDIT or DEBIT),{" "}
                <B>Record With Zero Balance</B> if you need zero rows, then
                Division → Branch. Click <B>Apply Filters</B>.
              </>,
              <>
                Click a customer row for the statement.{" "}
                <B>Export Report</B> → PDF or XLSX on both the list and the
                party detail.
              </>,
            ]}
          />
          <Definitions
            items={[
              {
                term: "List columns",
                desc: "Particulars, Debit, Credit",
              },
            ]}
          />
          <Screenshot caption="Debtors Ledger — customer balances, Type and zero-balance filters, party statement on click" />

          <h3
            id="section-1-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. Gross Profit
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Three tabs. Header stats: <B>Total Revenue</B>,{" "}
            <B>Total Profit</B>, <B>Profit Margin</B>. There is no Export
            Report on this screen.
          </p>
          <Flow
            steps={[
              "Reports",
              "Gross Profit",
              "pick a tab",
              "date / Division / Branch",
              "Apply Filters",
              "click a row to drill",
            ]}
          />
          <Steps
            items={[
              <>
                Open a tab: <B>Customer Wise Gross Profit</B>,{" "}
                <B>Item Wise Gross Profit</B>, or{" "}
                <B>Customer&apos;s Month Wise Gross Profit</B>.
              </>,
              <>
                Set date range (month range on Customer&apos;s Month Wise),
                Division → Branch. On Item Wise also set Item Group and Item
                Type (RM, FG, SEMI RM, CONSUMABLE, TRADE, SCRAP). Click{" "}
                <B>Apply Filters</B>.
              </>,
              <>
                Click a row to drill. Customer path: customer → documents →
                batch / production order. Item path: item → item detail →
                batch.
              </>,
            ]}
          />
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-2">
            Customer&apos;s Month Wise date presets:
          </p>
          <Flow
            steps={[
              "Last 3 Months",
              "Last 6 Months",
              "Last 9 Months",
              "Last 12 Months",
              "Custom Range",
            ]}
          />
          <Screenshot caption="Gross Profit — three tabs, revenue / profit / margin stats, drill into a customer or item" />

          <h3
            id="section-1-sub-8"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            9. Aging Reports
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Three reports in this group. Buckets are not fixed 0–30 / 31–60.
            You set how buckets are built, then click an amount to see the
            documents. No export on these screens.
          </p>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-2">
            How buckets are built on every aging report:
          </p>
          <Flow
            steps={[
              "Column Type (Days / Weeks / Months)",
              "Number of Columns (1–8)",
              "Duration",
              "Apply Filters",
            ]}
          />

          <p className="text-[16px] geist-medium text-[#111827] mb-2 mt-2">
            Customer Aging Report
          </p>
          <Flow
            steps={[
              "Reports",
              "Customer Aging Report",
              "date / Branch / Customer / buckets",
              "Apply Filters",
              "click a bucket",
              "Customer Aging Bucket",
            ]}
          />
          <Steps
            items={[
              <>
                Date range defaults to last month through today. Set Branch and
                Customer if you want a subset.
              </>,
              <>
                Number of Columns defaults to 5. Duration defaults to 5. Click{" "}
                <B>Apply Filters</B>, then click a bucket amount.
              </>,
            ]}
          />

          <p className="text-[16px] geist-medium text-[#111827] mb-2 mt-4">
            Vendor Aging Report
          </p>
          <Flow
            steps={[
              "Reports",
              "Vendor Aging Report",
              "date / Branch / Vendor / buckets",
              "Apply Filters",
              "click a bucket",
              "bucket list",
            ]}
          />
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-4">
            Same bucket controls as Customer Aging. Use <B>Vendor</B> instead
            of Customer.
          </p>

          <p className="text-[16px] geist-medium text-[#111827] mb-2">
            Receivables / Payables
          </p>
          <Flow
            steps={[
              "Reports",
              "Receivables / Payables",
              "date / Branch / buckets",
              "Apply Filters",
              "click Amount Receivable or Amount Payable",
            ]}
          />
          <Steps
            items={[
              <>
                Date range defaults to today through 30 days ahead (a forward
                window, not last month). Set Branch, Number of Columns, Column
                Type, Duration. Click <B>Apply Filters</B>.
              </>,
              <>
                Click <B>Amount Receivable</B> for that bucket&apos;s
                receivables list, or <B>Amount Payable</B> for payables.
              </>,
            ]}
          />
          <Definitions
            items={[
              {
                term: "Columns",
                desc: "Bucket, Amount Receivable, Amount Payable, Total Count, Receivables Count, Payables Count",
              },
            ]}
          />
          <Callout type="note">
            Aging defaults last month → today. Receivables / Payables defaults
            today → +30 days. Both still need Apply Filters after you change
            the range.
          </Callout>
          <Screenshot caption="Aging — Column Type, Number of Columns, Duration; click a bucket amount to drill" />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-2"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[28%]">
                    Topic
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((row) => (
                  <tr
                    key={row.topic}
                    className="border-b border-[#E5E5E5] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 text-[14px] geist-medium text-[#111827] align-top">
                      {row.topic}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151]">
                      {row.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/accountant/chart-of-accounts"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Chart of Accounts <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Account statement is a CoA row click — not Reports → General
              Ledger
            </p>
          </Link>
          <Link
            href="/documentation/books/inventory/view-inventory"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              View Inventory <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              On-hand qty and value. Period In/Out is Item Ledger
            </p>
          </Link>
          <Link
            href="/documentation/books/analytics/analytics-overview"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Analytics <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Charts and KPIs — a different module from this catalog
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
